/**
 * Bot Detection Utility
 * Detects bots on the frontend before they make requests
 * Uses backend configuration for bot blocking rules
 */

// Cache bot blocking settings from backend
let botBlockingSettings = null;
const BOT_SETTINGS_CACHE_KEY = 'bot_blocking_settings';
const BOT_SETTINGS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Load bot blocking settings from cache or backend
 */
export const loadBotBlockingSettings = async () => {
  // Check cache first
  try {
    const cached = localStorage.getItem(BOT_SETTINGS_CACHE_KEY);
    if (cached) {
      const { settings, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < BOT_SETTINGS_CACHE_TTL) {
        botBlockingSettings = settings;
        return settings;
      }
    }
  } catch (e) {
    // Cache invalid, fetch fresh
  }

  // Fetch from backend (will be done via maintenance mode check)
  // Settings will be populated from the maintenance mode response
  return botBlockingSettings;
};

/**
 * Set bot blocking settings (called from API response)
 */
export const setBotBlockingSettings = (settings) => {
  botBlockingSettings = settings;
  // Cache for future page loads
  try {
    localStorage.setItem(BOT_SETTINGS_CACHE_KEY, JSON.stringify({
      settings: settings,
      timestamp: Date.now()
    }));
  } catch (e) {
    // localStorage might be disabled
  }
};

/**
 * Get cached bot blocking settings
 */
export const getBotBlockingSettings = () => {
  // Try to load from cache if not already loaded
  if (!botBlockingSettings) {
    try {
      const cached = localStorage.getItem(BOT_SETTINGS_CACHE_KEY);
      if (cached) {
        const { settings, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < BOT_SETTINGS_CACHE_TTL) {
          botBlockingSettings = settings;
        }
      }
    } catch (e) {
      // Cache invalid
    }
  }
  return botBlockingSettings;
};

/**
 * Check if the current user agent is a bot
 */
export const isBot = () => {
  if (typeof window === 'undefined' || !window.navigator) {
    return false;
  }

  const userAgent = window.navigator.userAgent;
  if (!userAgent) {
    return false;
  }

  const botPatterns = [
    'bot', 'crawl', 'spider', 'scraper', 'curl', 'wget', 'python', 'java',
    'go-http', 'php', 'ruby', 'perl', 'node', 'axios', 'postman',
    'googlebot', 'bingbot', 'slurp', 'duckduckbot', 'baiduspider',
    'yandexbot', 'sogou', 'exabot', 'facebookexternalhit', 'twitterbot',
    'rogerbot', 'linkedinbot', 'embedly', 'quora link preview',
    'showyoubot', 'outbrain', 'pinterest', 'slackbot', 'vkShare',
    'W3C_Validator', 'whatsapp', 'flipboard', 'tumblr', 'bitlybot',
    'skypeuripreview', 'nuzzel', 'discordbot', 'qwantify', 'pinterestbot',
    'bitrix link preview', 'xing-contenttabreceiver', 'chrome-lighthouse',
    'applebot', 'petalbot', 'semrushbot', 'ahrefsbot', 'dotbot'
  ];

  const userAgentLower = userAgent.toLowerCase();
  return botPatterns.some(pattern => userAgentLower.includes(pattern));
};

/**
 * Get bot name from user agent
 */
export const getBotName = () => {
  if (typeof window === 'undefined' || !window.navigator) {
    return null;
  }

  const userAgent = window.navigator.userAgent;
  if (!userAgent) {
    return null;
  }

  const knownBots = {
    'googlebot': 'Google Bot',
    'bingbot': 'Bing Bot',
    'slurp': 'Yahoo Slurp',
    'duckduckbot': 'DuckDuckGo Bot',
    'baiduspider': 'Baidu Spider',
    'yandexbot': 'Yandex Bot',
    'facebookexternalhit': 'Facebook Bot',
    'twitterbot': 'Twitter Bot',
    'linkedinbot': 'LinkedIn Bot',
    'applebot': 'Apple Bot',
    'petalbot': 'Petal Bot',
    'semrushbot': 'SEMrush Bot',
    'ahrefsbot': 'Ahrefs Bot',
    'dotbot': 'DotBot',
    'curl': 'cURL',
    'wget': 'wget',
    'python': 'Python Bot',
    'postman': 'Postman',
  };

  const userAgentLower = userAgent.toLowerCase();
  for (const [pattern, name] of Object.entries(knownBots)) {
    if (userAgentLower.includes(pattern)) {
      return name;
    }
  }

  return 'Unknown Bot';
};

/**
 * Check additional bot indicators
 */
export const hasBotIndicators = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check for common bot indicators
  const indicators = [
    // No window properties that bots typically lack
    () => !window.chrome && !window.safari,
    // No plugins
    () => !window.navigator.plugins || window.navigator.plugins.length === 0,
    // Missing common browser APIs
    () => !window.localStorage,
    () => !window.sessionStorage,
    // WebDriver indicator
    () => window.navigator.webdriver === true,
    // Headless Chrome indicators
    () => window.chrome && (!window.chrome.runtime || !window.chrome.runtime.onConnect),
  ];

  return indicators.some(check => {
    try {
      return check();
    } catch {
      return false;
    }
  });
};

/**
 * Determine if current request should be blocked on frontend
 * This prevents bots from making API calls and consuming bandwidth
 * Uses backend configuration if available, falls back to basic detection
 */
export const shouldBlockBot = () => {
  // Check if it's a bot first
  if (!isBot()) {
    return false;
  }

  // Get bot blocking settings from backend (or cache)
  const settings = getBotBlockingSettings();

  // If we have backend settings, use them
  if (settings) {
    const botName = getBotName();

    // Check "Block All Bots" setting
    if (settings.block_all_bots) {
      return true;
    }

    // Check "Block Unknown Bots" setting
    if (settings.block_unknown_bots && botName === 'Unknown Bot') {
      return true;
    }

    // Check specific blocked bots
    if (settings.blocked_bots && Array.isArray(settings.blocked_bots) && settings.blocked_bots.length > 0) {
      // Case-insensitive comparison
      const normalizedBotName = botName ? botName.trim().toLowerCase() : '';
      const isBlocked = settings.blocked_bots.some(blockedBot => {
        return blockedBot && blockedBot.trim().toLowerCase() === normalizedBotName;
      });
      if (isBlocked) {
        return true;
      }
    }

    // If settings exist but bot is not blocked by any rule, allow it
    return false;
  }

  // Fallback: If no backend settings available yet, use basic detection
  // This prevents bots from making API calls while we fetch settings
  // Check bot indicators for additional confirmation
  return hasBotIndicators() || isBot();
};

